import { useCallback, useMemo, useRef, useState } from 'react';
import { TableV3FilterItem } from 'common/table/types/TableV3';

type UseTableFilterApplyParams = {
  requiresApply: boolean;
  committedValues: string[];
  getSelectedItems: () => TableV3FilterItem[];
  setSelection: (values: string[]) => void;
  onFilterApply?: (items: TableV3FilterItem[]) => void;
};

export const useTableFilterApply = ({
  requiresApply,
  committedValues,
  getSelectedItems,
  setSelection,
  onFilterApply
}: UseTableFilterApplyParams) => {
  const [open, setOpen] = useState(false);
  const pendingCommitRef = useRef<(() => void) | null>(null);
  const settledListenersRef = useRef(new Set<() => void>());

  const deferral = useMemo(
    () => ({
      defer: (commit: () => void) => {
        pendingCommitRef.current = commit;
      },
      subscribeToSettled: (listener: () => void) => {
        settledListenersRef.current.add(listener);
        return () => {
          settledListenersRef.current.delete(listener);
        };
      }
    }),
    []
  );

  const settle = useCallback((commitPending: boolean) => {
    if (commitPending) pendingCommitRef.current?.();
    pendingCommitRef.current = null;
    settledListenersRef.current.forEach((listener) => listener());
    setOpen(false);
  }, []);

  const apply = () => {
    settle(true);
    onFilterApply?.(getSelectedItems());
  };

  const hasChanges = () => {
    if (pendingCommitRef.current) return true;
    const picked = getSelectedItems().map((item) => item.value);
    return (
      picked.length !== committedValues.length ||
      picked.some((value) => !committedValues.includes(value))
    );
  };

  const cancel = () => {
    settle(false);
    setSelection(committedValues);
  };

  // Closing keeps what was picked, as Apply does. With nothing changed it
  // only closes, so the table does not refetch. Only Cancel discards.
  const close = () => {
    if (!requiresApply) {
      setOpen(false);
    } else if (hasChanges()) {
      apply();
    } else {
      settle(false);
    }
  };

  return {
    open,
    setOpen,
    deferral: requiresApply ? deferral : undefined,
    confirmActions: requiresApply
      ? { onApply: apply, onCancel: cancel, onClear: () => setSelection([]) }
      : undefined,
    toggleOpen: () => (open ? close() : setOpen(true)),
    dismiss: (event: MouseEvent | TouchEvent, anchor: HTMLElement | null) => {
      // The filter icon's own handler runs next; this is a toggle, not a dismiss.
      if (anchor?.contains(event.target as Node)) return;
      close();
    }
  };
};
